import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, bareApi, resetSessionFlags } from "../services/http";
import { useSessionSecurity } from "../hooks/useSessionSecurity";
import {
  HEARTBEAT_KEY,
  IDLE_TIMEOUT_MS,
  LAST_ACTIVITY_KEY,
  LOGOUT_BROADCAST_KEY,
  LOGOUT_REASON_KEY,
  RESUME_GRACE_MS,
  SESSION_START_KEY,
  clearSessionClock,
  readTimestamp,
  writeTimestamp,
} from "./sessionConstants";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const redirectToLogin = () => {
  window.location.replace("/login");
};

function touchSessionStart() {
  try {
    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    }
  } catch {
    /* ignore */
  }
}

function clearSessionStart() {
  try {
    sessionStorage.removeItem(SESSION_START_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Sessão que não se pode retomar sem login: o painel esteve fechado (o Chrome
 * reabre os separadores e repõe o sessionStorage) ou passou o tempo de inatividade.
 */
function resumeExpired() {
  const now = Date.now();
  const beat = readTimestamp(HEARTBEAT_KEY);
  const activity = readTimestamp(LAST_ACTIVITY_KEY);
  return beat == null || now - beat > RESUME_GRACE_MS || activity == null || now - activity >= IDLE_TIMEOUT_MS;
}

function setLogoutReason(text) {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, text);
  } catch {
    /* ignore */
  }
}

function hasSessionStart() {
  try {
    return Boolean(sessionStorage.getItem(SESSION_START_KEY));
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const broadcastLogoutToOtherTabs = useCallback(() => {
    try {
      localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }, []);

  const onRemoteLogout = useCallback(() => {
    resetSessionFlags();
    clearSessionStart();
    clearSessionClock();
    setUser(null);
    window.location.replace("/login");
  }, []);

  const login = useCallback(async (email, password, code) => {
    resetSessionFlags();
    const { data } = await authApi.post("/auth/login", { email, password, code });
    clearSessionStart();
    try {
      sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    writeTimestamp(LAST_ACTIVITY_KEY);
    writeTimestamp(HEARTBEAT_KEY);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    broadcastLogoutToOtherTabs();
    resetSessionFlags();
    clearSessionStart();
    clearSessionClock();
    try {
      await authApi.post("/auth/logout", {});
    } catch {
      // ignore
    }
    setUser(null);
    redirectToLogin();
  }, [broadcastLogoutToOtherTabs]);

  /** Super-admin: passa a trabalhar noutra igreja (recarrega para limpar dados em cache). */
  const switchChurch = useCallback(async (churchId, redirectTo = "/dashboard") => {
    const { data } = await authApi.post("/auth/switch-church", { churchId });
    setUser(data.user);
    window.location.assign(redirectTo);
  }, []);

  /** Actualiza campos do utilizador da sessão (ex.: depois de activar o 2FA). */
  const updateUser = useCallback((fields) => {
    setUser((prev) => (prev ? { ...prev, ...fields } : prev));
  }, []);

  /** Actualiza a igreja da sessão (ex.: depois de mudar nome, cor ou logótipo). */
  const updateChurch = useCallback((church) => {
    setUser((prev) => (prev ? { ...prev, church: { ...prev.church, ...church } } : prev));
  }, []);

  const logoutAll = useCallback(async () => {
    broadcastLogoutToOtherTabs();
    resetSessionFlags();
    clearSessionStart();
    clearSessionClock();
    try {
      await authApi.post("/auth/logout-all");
    } catch {
      // ignore
    }
    setUser(null);
    redirectToLogin();
  }, [broadcastLogoutToOtherTabs]);

  async function bootstrap() {
    // Sem este marcador (sessionStorage: sobrevive a um F5 na mesma aba,
    // mas é sempre apagado ao fechar a aba/janela), nunca autenticamos
    // silenciosamente através do cookie de refresh — que por si só continua
    // válido vários dias. Sem isto, fechar e reabrir o separador entrava
    // directamente no painel sem pedir login, mesmo depois de muito tempo
    // sem actividade.
    if (!hasSessionStart()) {
      setUser(null);
      setReady(true);
      return;
    }
    // O navegador reabriu o separador depois de fechado, ou passou o tempo de
    // inatividade: termina a sessão também no servidor e pede login.
    if (resumeExpired()) {
      clearSessionStart();
      clearSessionClock();
      resetSessionFlags();
      setLogoutReason("A sessão terminou porque o painel esteve fechado ou sem actividade. Inicie sessão novamente.");
      try {
        await bareApi.post("/auth/logout", {});
      } catch {
        /* ignore */
      }
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const { data } = await bareApi.get("/auth/me");
      resetSessionFlags();
      setUser(data.user);
      touchSessionStart();
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await bareApi.post("/auth/refresh", {});
          const { data } = await bareApi.get("/auth/me");
          resetSessionFlags();
          setUser(data.user);
          touchSessionStart();
        } catch {
          resetSessionFlags();
          clearSessionStart();
          setUser(null);
        }
      } else {
        resetSessionFlags();
        clearSessionStart();
        setUser(null);
      }
    } finally {
      setReady(true);
    }
  }

  useEffect(() => {
    bootstrap();
  }, []);

  useSessionSecurity({
    user,
    ready,
    logout,
    onRemoteLogout,
  });

  const value = useMemo(
    () => ({ user, ready, login, logout, logoutAll, switchChurch, updateChurch, updateUser }),
    [user, ready, login, logout, logoutAll, switchChurch, updateChurch, updateUser]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
